import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import AuthShell from "@/components/marketplace/AuthShell";
import { FIELD_STYLES } from "@/pages/LoginPage";
import api from "@/lib/api";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [devLink, setDevLink] = useState(null);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(true);
      if (data?.dev_reset_link) setDevLink(data.dev_reset_link);
      toast.success("If an account exists, we've sent a reset link.");
    } catch (_) {
      toast.error("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Forgot your password?" subtitle="Enter your email and we'll send you a reset link.">
      <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
        <Field label="Email">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            data-testid="forgot-email"
            className="input-primary"
          />
        </Field>
        <button
          type="submit"
          disabled={loading}
          data-testid="forgot-submit"
          className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#165a71] text-white px-6 py-3 font-medium hover:bg-[#11485a] disabled:opacity-60 transition-colors"
        >
          {loading ? "Sending…" : (<>Send reset link <ArrowRight className="h-4 w-4" /></>)}
        </button>
        {sent && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900" data-testid="forgot-success">
            If <b>{email}</b> is on file, a reset link is on its way.
            {devLink && (
              <div className="mt-2 text-xs text-emerald-900/80 break-all">
                Dev link: <Link to={devLink} className="underline">{devLink}</Link>
              </div>
            )}
          </div>
        )}
        <p className="text-sm text-center text-[#252A34]/70 pt-2">
          Remembered?{" "}
          <Link to="/login" className="font-medium text-[#165a71]" data-testid="forgot-back-to-login">Back to sign in</Link>
        </p>
      </form>
      <style>{FIELD_STYLES}</style>
    </AuthShell>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-sm font-medium text-[#071b22]">{label}</span>
    {children}
  </label>
);
